import React, { useEffect, useState } from 'react';

import { Wrapper } from '@pkg/components';
import { PrizeItem } from '@pkg/components/Sections/Admin/Fantasy/Prizes/PrizeItem';
import { Input } from '@pkg/components/Sections/Admin/components/Form';
import { Dropdown } from '@pkg/components/Sections/Admin/components/Form/Dropdown';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type Competition, type Prize } from '@app/client/common/structs';
import { DEFAULT_PRIZE_FORM_VALUES } from '@app/helpers';

export type RewardType = 'team' | 'player';

interface Props {
    rewardType: RewardType;
    setRewards: React.Dispatch<React.SetStateAction<Prize[]>>;
    rewards: Competition['prizes'] | Competition['team_prizes'];
}

const OTHER = 'Other';
const CURRENCIES = ['USD', 'ETH', 'USDC', 'USDT', 'BTC', 'MATIC', 'APT', OTHER];

export const ManagePrizes = ({ rewardType = 'player', rewards, setRewards }: Props) => {
    const [selectedCurrency, setSelectedCurrency] = useState<string>();
    const [currency, setCurrency] = useState<string>();

    useEffect(() => {
        const rewardCurrency = selectedCurrency === OTHER ? currency : selectedCurrency;
        const currReward = rewards?.[0]?.description;
        if (rewardCurrency) {
            if (currReward && currReward !== rewardCurrency) {
                setRewards((rewards) => rewards.map((reward) => ({ ...reward, description: rewardCurrency })));
            }
        } else if (currReward) {
            if (CURRENCIES.includes(currReward)) {
                setSelectedCurrency(currReward);
            } else {
                setSelectedCurrency(OTHER);
                setCurrency(currReward);
            }
        }
    }, [rewards, selectedCurrency, setRewards, currency]);

    function onAddReward() {
        setRewards((r: Prize[]) => [...r, DEFAULT_PRIZE_FORM_VALUES]);
    }

    function onRemoveReward(prizeToRemove: Prize) {
        setRewards((currentRewards) =>
            currentRewards.filter((cr) => cr.rank !== prizeToRemove.rank || cr.reward !== prizeToRemove.reward),
        );
    }

    function onUpdate(prizeToUpdate: Prize, newPrizeData: Prize) {
        setRewards((currentRewards) =>
            currentRewards.map((cr) => {
                if (cr.reward === prizeToUpdate.reward && cr.rank === prizeToUpdate.rank) {
                    return newPrizeData;
                }
                return cr;
            }),
        );
    }

    function handleCurrency(e) {
        setSelectedCurrency(e.target.value);
    }

    return (
        <Wrapper width="45%">
            <Dropdown onChange={handleCurrency} value={selectedCurrency} defaultValue={''}>
                <option value="" disabled>
                    Select a currency
                </option>
                {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                        {c.toUpperCase()}
                    </option>
                ))}
            </Dropdown>
            {selectedCurrency === OTHER ? (
                <Input placeholder="Custom currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
            ) : null}
            <Wrapper width={'200px'} margin="10px 0 5px auto">
                <Button variant="solid" color="success" size="xsmall" type="button" onClick={onAddReward}>
                    Add {rewardType} reward
                </Button>
            </Wrapper>
            {rewards && rewards?.length
                ? rewards?.map((r) => (
                      <PrizeItem
                          currency={selectedCurrency === OTHER ? currency : selectedCurrency}
                          key={`${rewardType}-${r.reward}-${r.rank}`}
                          onUpdate={onUpdate}
                          rewardType={rewardType}
                          prize={r}
                          canEdit
                          onRemoveReward={onRemoveReward}
                      />
                  ))
                : `No ${rewardType} rewards`}
        </Wrapper>
    );
};
